<template>
    <section class="sales-orders-section-wrapper">
        <div class="section-header">
            <h4>{{ $t("message.salesOrders") }}</h4>
        </div>

        <div v-if="salesOrders.length" class="sales-orders-section">
            <div v-for="order in salesOrders" :key="order.id" class="order-row">
                <div class="order-info">
                    <div class="order-subject">
                        <i class="material-icons">shopping_cart</i>
                        <span>{{ order.Subject }}</span>
                    </div>
                    <div class="order-status" v-if="order.Status">
                        <i class="material-icons">label</i>
                        <span>{{ order.Status }}</span>
                    </div>
                    <div class="order-footer">
                        <div class="order-owner">
                            <i class="material-icons">person</i>
                            <span>{{ $_.get(order, "Owner.name", "") }}</span>
                        </div>
                        <div class="order-number" v-if="order.SO_Number">
                            <i class="material-icons">tag</i>
                            <span>{{ order.SO_Number }}</span>
                        </div>
                    </div>
                    <div class="order-date">
                        <i class="material-icons">event</i>
                        <span>{{ formatDate(order.Created_Time) }}</span>
                    </div>
                </div>
                <SupsisButton class="view-order-btn" icon="open_in_new" @click="gotoSalesOrderOnZoho(order.id)" />
                <a-divider />
            </div>
        </div>

        <div v-else-if="loading" class="loading-container">
            <span>{{ $t("message.loadingSalesOrders") }}</span>
            <BucketSpinner class="large-spinner" />
        </div>

        <div v-else class="empty-orders-container">
            <span class="material-icons empty-icon">shopping_cart</span>
            <p class="empty-text">{{ $t("message.noSalesOrdersFound") }}</p>
        </div>
    </section>
</template>

<script>
import Vue from "vue";
import { ZOHO_API } from "../../../../api/zoho";
import { mapState } from "vuex";
import BucketSpinner from "../../../../views/Reports/common/spinners/BucketSpinner.vue";
import SupsisButton from "../../../../views/Pages/Settings/components/SupsisButton.vue";

export default Vue.extend({
    name: "ZohoSalesOrdersSection",
    components: {
        BucketSpinner,
        SupsisButton,
    },
    props: {
        contact: {
            type: Object,
            required: true,
        },
    },
    computed: {
        ...mapState({
            zohoChannelExists: (state) => !!state.channel.channels.find((x) => x.type === "zoho" && !!x.isDefault),
            zohoChannel: (state) => state.channel.channels.find((x) => x.type === "zoho" && !!x.isDefault),
        }),
    },
    data() {
        return {
            salesOrders: [],
            loading: false,
        };
    },
    async created() {
        await this.fetchSalesOrders();
    },
    methods: {
        async fetchSalesOrders() {
            this.loading = true;
            try {
                const contactId = this.contact._id;
                const zohoContactId = this.contact.zohoInfo?.contactId;
                const phone = this.contact.whatsappNumber || this.contact.phone;
                
                const salesOrders = zohoContactId
                    ? await ZOHO_API.searchRecordsByContactId("Sales_Orders", zohoContactId)
                    : await ZOHO_API.searchRecordsByPhone("Sales_Orders", phone);
                    
                if (this.contact?._id === contactId) {
                    this.salesOrders = Array.isArray(salesOrders) ? salesOrders : [];
                }
            } catch (error) {
            } finally {
                this.loading = false;
            }
        },
        formatDate(dateString) {
            if (!dateString) return "";
            return new Date(dateString).toLocaleDateString("tr-TR", {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        },
        gotoSalesOrderOnZoho(orderId) {
            const orgId = _.get(this.zohoChannel, "payload.organization.domain_name");
            if (orgId && orderId) {
                window.open(`https://crm.zoho.com/crm/${orgId}/tab/SalesOrders/${orderId}`, "_blank");
            }
        },
    },
    watch: {
        contact: {
            handler() {
                this.salesOrders = [];
                this.fetchSalesOrders();
            },
            deep: true,
        },
    },
});
</script>

<style scoped lang="scss">
.sales-orders-section-wrapper {
    .section-header {
        margin-bottom: 12px;

        h4 {
            font-size: 16px;
            font-weight: 500;
            color: #4a5568;
            margin: 0;
        }
    }

    .sales-orders-section {
        .order-row {
            position: relative;
            padding: 12px;

            .order-info {
                .order-subject,
                .order-status,
                .order-date,
                .order-footer > div {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    margin-bottom: 4px;

                    i.material-icons {
                        font-size: 18px;
                        color: #64748b;
                    }
                }

                .order-subject {
                    font-weight: 500;
                    font-size: 15px;
                    color: #2d3748;
                }

                .order-status {
                    display: inline-flex;
                    background: #e2e8f0;
                    padding: 2px 8px;
                    border-radius: 12px;
                    font-size: 0.85em;
                    color: #4a5568;
                }

                .order-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 0.9em;

                    .order-owner {
                        color: #718096;
                    }

                    .order-number {
                        color: #718096;
                        font-weight: 500;
                    }
                }

                .order-date {
                    font-size: 0.85em;
                    color: #94a3b8;
                }
            }

            .view-order-btn {
                position: absolute;
                top: 12px;
                right: 12px;
            }
        }
    }

    .loading-container,
    .empty-orders-container {
        min-height: 100px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 20px;
        color: #94a3b8;

        .empty-icon {
            font-size: 36px;
            margin-bottom: 8px;
        }

        .empty-text {
            font-size: 14px;
            text-align: center;
            margin: 0;
        }
    }
}
</style>

